import React, { Suspense, useRef, useState, useEffect, useMemo, useLayoutEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import * as THREE from 'three';
import ErrorBoundary from "@shared_components/components/studio/ErrorBoundary";
import { ModelFile, ModelFileMaterial } from "adminportal/src/pages/ProductManagement/DesignSets/types";
import { useGLTF, Environment, OrbitControls, ContactShadows, Html } from "@react-three/drei"; // REMOVED useTexture
import { FaCompress, FaExpand, FaEye, FaEyeSlash } from "react-icons/fa";
import { TemporaryPanel } from "../../../../teamsite/src/pages/NewAiDesign/types";
import { Canvas } from "@react-three/fiber";
import { generateCompositedPanelDataUrl } from "./utils";
import { useMobileCheck } from "../../functions/mobileCheck";

type AiDesignStudioWrapperProps = {
    gltf_file: ModelFile | null;
    panels: TemporaryPanel[];
    preserve_drawing_buffer?: boolean;
}

export interface AiDesignStudioWrapperRef {
    captureInitialViewPng: () => Promise<string | null>;
}

const waitForNextFrame = () => new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
});

// used by teams site ai design and gearstudio
export const AiDesignStudioWrapper = forwardRef<AiDesignStudioWrapperRef, AiDesignStudioWrapperProps>(({
    gltf_file,
    panels,
    preserve_drawing_buffer = false,
}, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const orbitControlsRef = useRef<any>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const isMobile = useMobileCheck();
    const [isMannequinHidden, setIsMannequinHidden] = useState(false);
    const [hasMannequin, setHasMannequin] = useState(false);
    const [object_positions, setObjectPositions] = useState<{ scale: number; x: number; y: number; z: number; }>({ scale: 1, x: 0, y: 0, z: 0 });
    const camera_positions = useMemo(() => {
        const base = gltf_file?.camera_positions || { x: 0, y: 25, z: 50, zoom: 350 };
        const zoomOffset = isMobile ? -300 : 0;

        return {
            ...base,
            zoom: Math.max(1, base.zoom + zoomOffset),
        };
    }, [gltf_file, isMobile]);
   // console.log("is mobile?", isMobile, "camera positions:", camera_positions);
    useEffect(() => {
        if (gltf_file && gltf_file.object_positions) {
            setObjectPositions(gltf_file.object_positions);
        }
    }, [gltf_file]);

    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch((err) => console.error(err));
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const captureInitialViewPng = useCallback(async () => {
        const canvas = canvasRef.current || (
            containerRef.current?.querySelector(".canvas-container canvas") as HTMLCanvasElement | null
        );
        if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
            return null;
        }

        const controls = orbitControlsRef.current;
        if (!controls || typeof controls.reset !== "function") {
            try {
                return canvas.toDataURL("image/png");
            } catch (_error) {
                return null;
            }
        }

        const camera = controls.object as THREE.Camera & {
            zoom?: number;
            updateProjectionMatrix?: () => void;
        };
        const previousPosition = camera.position.clone();
        const previousQuaternion = camera.quaternion.clone();
        const previousZoom = typeof camera.zoom === "number" ? camera.zoom : undefined;
        const previousTarget = controls.target?.clone?.();

        try {
            controls.reset();
            controls.update?.();
            await waitForNextFrame();
            await waitForNextFrame();
            return canvas.toDataURL("image/png");
        } catch (_error) {
            return null;
        } finally {
            camera.position.copy(previousPosition);
            camera.quaternion.copy(previousQuaternion);
            if (typeof previousZoom === "number") {
                camera.zoom = previousZoom;
                camera.updateProjectionMatrix?.();
            }
            if (previousTarget && controls.target?.copy) {
                controls.target.copy(previousTarget);
            }
            controls.update?.();
        }
    }, []);

    useImperativeHandle(ref, () => ({
        captureInitialViewPng,
    }), [captureInitialViewPng]);

    if (!gltf_file) return <div>Loading Data...</div>;
    //  console.log("Rendering AiDesignStudio with GLTF:", gltf_file, "and panels:", panels);

  
    return (
        <div className="flex flex-col w-full items-center h-full">
            <div
                ref={containerRef}
                className={`w-full relative bg-gray-100 rounded-lg overflow-hidden transition-all duration-300 ${isFullscreen ? "h-screen w-screen fixed top-0 left-0 z-50" : "h-full w-full"}`}
            >
                <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
                    <button
                        onClick={() => setIsMannequinHidden((prev) => !prev)}
                        className="bg-white/80 p-2 rounded-full shadow-lg hover:bg-white transition-colors backdrop-blur-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!hasMannequin}
                        title={hasMannequin ? "Toggle mannequin visibility" : "No mannequin found in model"}
                    >
                        {isMannequinHidden ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                    </button>
                    <button
                        onClick={toggleFullscreen}
                        className="bg-white/80 p-2 rounded-full shadow-lg hover:bg-white transition-colors backdrop-blur-sm cursor-pointer"
                        title="Toggle fullscreen"
                    >
                        {isFullscreen ? <FaCompress className="w-5 h-5" /> : <FaExpand className="w-5 h-5" />}
                    </button>
                </div>

                <div className="canvas-container h-full w-full" >
                    <ErrorBoundary fallback={<div>Error Loading 3D Model</div>}>

                        <Canvas
                            shadows
                            orthographic
                            camera={{ position: [camera_positions.x, camera_positions.y, camera_positions.z], zoom: camera_positions.zoom }}
                            gl={{
                                antialias: true,
                                toneMapping: THREE.ACESFilmicToneMapping,
                                preserveDrawingBuffer: preserve_drawing_buffer,
                            }}
                            onCreated={(state) => {
                                canvasRef.current = state.gl.domElement;
                            }}
                        >
                            {/* Suspense is only triggered on initial model load now */}
                            <Suspense fallback={<Html center>Loading Studio...</Html>} >
                                <ModelHandler
                                    panels={panels}
                                    gltf_file={gltf_file.gltf_file}
                                    product_materials={gltf_file.materials}
                                    autoRefresh={true}
                                    positions={object_positions}
                                    hideMannequin={isMannequinHidden}
                                    onMannequinPresenceChange={setHasMannequin}
                                    orbitControlsRef={orbitControlsRef}
                                />
                            </Suspense>
                        </Canvas>
                    </ErrorBoundary>

                </div>

            </div>
        </div>
    )
});
AiDesignStudioWrapper.displayName = "AiDesignStudioWrapper";


interface ModelHandlerProps {
    autoRefresh: boolean;
    gltf_file: string;
    panels: TemporaryPanel[];
    product_materials: ModelFileMaterial[];
    positions: {
        scale: number;
        x: number;
        y: number;
        z: number;
    },
    hideMannequin: boolean;
    onMannequinPresenceChange?: (hasMannequin: boolean) => void;
    orbitControlsRef: React.MutableRefObject<any>;
}
export interface ModelHandlerRef {
    triggerRefresh: () => void;
}

const ModelHandler = forwardRef<ModelHandlerRef, ModelHandlerProps>(
    ({ autoRefresh, gltf_file, panels, product_materials, positions, hideMannequin, onMannequinPresenceChange, orbitControlsRef }, ref) => {
        const { nodes, materials: originalMaterials } = useGLTF(gltf_file);

        // REPLACEMENT: Instead of storing URLs, we store the actual Texture objects
        // This allows us to load them silently without triggering Suspense
        const [loadedTextures, setLoadedTextures] = useState<Map<string, THREE.Texture>>(new Map());
        
        const [processedPanels, setProcessedPanels] = useState<Map<string, TemporaryPanel>>(new Map());
        const [isLoadingPanels, setIsLoadingPanels] = useState(true);
        const [initialLoad, setInitialLoad] = useState(true);

        useEffect(() => {
            if (!autoRefresh && processedPanels.size > 0) {
                setIsLoadingPanels(false);
                return;
            }
            if (panels) {
                generatePanels();
            }
        }, [panels, autoRefresh]);

        useImperativeHandle(ref, () => ({
            triggerRefresh() {
                generatePanels();
            },
        }));

        const generatePanels = async () => {
            let panelsNeedUpdate = false;
            
            // We track textures to update here
            const newTexturesToLoad = new Map<string, string>(); // Key -> DataUrl
            const newProcessedPartials = new Map<string, { panel: TemporaryPanel, toDelete: boolean }>();

            for (const panel of panels) {
                if (!panel.partkey) continue;
                const previousVersion = processedPanels.get(panel.partkey);
                if (!previousVersion || JSON.stringify(previousVersion) !== JSON.stringify(panel)) {
                    panelsNeedUpdate = true;
                    newProcessedPartials.set(panel.partkey, { panel, toDelete: false });
                }
            }

            const currentPanelKeys = new Set(panels.map(t => t.partkey));
            for (const oldKey of processedPanels.keys()) {
                if (!currentPanelKeys.has(oldKey)) {
                    panelsNeedUpdate = true;
                    newProcessedPartials.set(oldKey, { panel: processedPanels.get(oldKey)!, toDelete: true });
                }
            }

            if (!panelsNeedUpdate) {
                setIsLoadingPanels(false);
                setInitialLoad(false);
                return;
            }

            // 1. Generate Data URLs for changed panels
            await Promise.all(
                Array.from(newProcessedPartials.entries()).map(async ([partkey, { panel, toDelete }]) => {
                    if (toDelete) return;

                    let url = "";
                    if ((panel.images && panel.images.length > 0)) {
                        try {
                            url = await generateCompositedPanelDataUrl(panel);
                        } catch (error) {
                            console.error(`Failed to generate composite panel for ${partkey}`, error);
                        }
                    } else if (panel?.images_data?.webp || panel?.images_data?.src) {
                        url = panel?.images_data?.webp || panel?.images_data?.src || "";
                    }

                    if (url) {
                        newTexturesToLoad.set(partkey, url);
                    }
                })
            );

            // 2. Manually Load Textures (Async, no Suspense)
            // This prevents the "Loading Studio..." flicker
            const textureLoader = new THREE.TextureLoader();
            const loadedTextureMap = new Map<string, THREE.Texture>();

            await Promise.all(
                Array.from(newTexturesToLoad.entries()).map(async ([key, url]) => {
                    try {
                        const texture = await textureLoader.loadAsync(url);
                        // Apply necessary settings immediately
                        texture.colorSpace = THREE.SRGBColorSpace;
                        texture.flipY = false;
                        loadedTextureMap.set(key, texture);
                    } catch (e) {
                        console.error("Failed to load texture for", key, e);
                    }
                })
            );

            // 3. Batch Update State
            setProcessedPanels(prev => {
                const updated = new Map(prev);
                newProcessedPartials.forEach(({ panel, toDelete }, key) => {
                    if (toDelete) updated.delete(key);
                    else updated.set(key, panel);
                });
                return updated;
            });

            setLoadedTextures(prev => {
                const updated = new Map(prev);
                
                // Remove deleted textures
                newProcessedPartials.forEach(({ toDelete }, key) => {
                    if (toDelete) {
                        const oldTex = updated.get(key);
                        if (oldTex) oldTex.dispose(); // Cleanup memory
                        updated.delete(key);
                    }
                });

                // Add/Replace new textures
                loadedTextureMap.forEach((tex, key) => {
                    // Dispose old texture if replacing
                    const oldTex = updated.get(key);
                    if (oldTex) oldTex.dispose();
                    
                    updated.set(key, tex);
                });
                
                return updated;
            });

            setIsLoadingPanels(false);
            setInitialLoad(false);
        };

        const processedMaterials = useMemo(() => {
            const newMaterials = { ...originalMaterials };

            product_materials.forEach(materialInfo => {
                if (materialInfo.replaceable && loadedTextures.has(materialInfo.name)) {
                    // Retrieve texture directly from our manual state map
                    const newPanel = loadedTextures.get(materialInfo.name)!;

                    if (newPanel) {
                        newMaterials[materialInfo.name] = new THREE.MeshStandardMaterial({
                            map: newPanel,
                            name: materialInfo.name,
                            roughness: 0.7,
                            metalness: 0.0,
                            side: THREE.DoubleSide,
                            envMapIntensity: 0.2
                        });
                    }
                }
            });

            return newMaterials;
        }, [originalMaterials, product_materials, loadedTextures]); // Depend on loadedTextures instead of URLs

        const meshes = useMemo(() =>
            Object.values(nodes).filter((node): node is THREE.Mesh =>
                node.type === 'Mesh' || node instanceof THREE.Mesh
            ), [nodes]);

        useEffect(() => {
            if (!onMannequinPresenceChange) return;
            const hasMannequinMesh = meshes.some((mesh) => mesh.name === "MANNEQUIN");
            onMannequinPresenceChange(hasMannequinMesh);
        }, [meshes, onMannequinPresenceChange]);

        if (isLoadingPanels && initialLoad) {
            return <Loader />;
        }

        //console.log(nodes)

        return (
            <>
                <Environment preset="city" blur={1} environmentIntensity={0.5} />
                <directionalLight
                    position={[5, 5, 5]}
                    intensity={1.5}
                    castShadow
                    shadow-mapSize={[2048, 2048]}
                    shadow-bias={-0.0001}
                >
                    <orthographicCamera attach="shadow-camera" args={[-10, 10, 10, -10]} />
                </directionalLight>
                <directionalLight position={[-5, 0, 5]} intensity={0.5} color="white" />
                <directionalLight position={[0, 5, 0]} intensity={0.5} color="white" />
                <spotLight
                    position={[0, 5, -10]}
                    intensity={2.0}
                    angle={0.5}
                    penumbra={1}
                    castShadow={false}
                />

                <ContactShadows position={[0, -0.01, 0]} opacity={0.4} scale={15} blur={2.5} far={1} />
                <group dispose={null} scale={positions.scale} position={[positions.x, positions.y, positions.z]}>
                    {meshes.map((mesh) => {
                        if (hideMannequin && mesh.name === "MANNEQUIN") {
                            return null;
                        }
                        const materialName = Array.isArray(mesh.material) ? undefined : mesh.material?.name;
                        const material = materialName ? (Array.isArray(mesh.material) ? undefined : processedMaterials[materialName]) : undefined;
                        mesh.castShadow = true;
                        mesh.receiveShadow = true;

                        return (
                            <MemoizedMesh
                                key={mesh.uuid}
                                geometry={mesh.geometry}
                                material={material}
                            />
                        );
                    })}
                </group>
                <OrbitControls ref={orbitControlsRef} makeDefault enablePan={true} enableZoom={true} enableRotate={true} />
            </>
        );
    }
);

type MemoizedMeshProps = {
    geometry: THREE.BufferGeometry;
    material?: THREE.Material | THREE.Material[];
};

const MemoizedMesh = React.memo(function MemoizedMesh({ geometry, material }: MemoizedMeshProps) {
    return (
        <mesh
            castShadow
            receiveShadow
            geometry={geometry}
            material={material}
        />
    );
});

const Loader: React.FC = () => {
  return (
    <Html center>
      <div className="text-gray-600 bg-white/70 p-3 rounded-md shadow">
        Loading 3D Model...
      </div>
    </Html>
  );
}
